import React, { ReactNode } from 'react';
import styled from 'styled-components';

import { BUTTON_TYPES, CARD_TYPES } from '../../../shared/constants';
import { IContentfulLink, IImage } from '../../../types/shared';

import SecondaryButton from '../../atoms/secondary-button/SecondaryButton';

import { DEQ_TYPES } from '../../../utils/deq';
import { getCtaLink } from '../../../utils/generic';

import mq from '../../../styles/breakpoints';
import Colors from '../../../styles/colors';
import { Body, H3 } from '../../../styles/generic';
import Spacings from '../../../styles/Spacings';

interface Props {
  image?: IImage;
  secondaryLink: IContentfulLink;
  children: ReactNode;
  title?: string;
}

const LinkCard = ({ image, title, secondaryLink, children }: Props) => (
  <Component id={CARD_TYPES.linkCard}>
    {image?.file && (
      <ImageContainer>
        <img data-testid="image" alt="" src={image.file?.url} />
      </ImageContainer>
    )}
    {title && <Title aria-level={3}>{title}</Title>}
    <StyledBody>{children}</StyledBody>
    {secondaryLink && (
      <StyledSecondaryButton
        textAlign="left"
        label={secondaryLink.label}
        to={getCtaLink(secondaryLink) as string}
        icon={secondaryLink.icon || 'checvron'}
        type={BUTTON_TYPES.internal}
        className="secondary-button"
        deqCustomAttributes={{
          componentType: 'LinkCard',
          type: DEQ_TYPES.textLink,
          label: secondaryLink.label.toLowerCase(),
        }}
      />
    )}
  </Component>
);

export default LinkCard;

const Component = styled.div`
  background: ${Colors.white};
  box-shadow: ${({ theme }) => theme.elevations.abnElevationWell};
  box-sizing: border-box;
  display: flex;
  flex: 1;
  flex-direction: column;
  height: auto;
  height: auto;
  padding: ${Spacings.S};
  width: 100%;

  ${mq.from.breakpoint.M`
    background: ${Colors.white};
    padding: ${Spacings.L};
    box-sizing: border-box;
    box-shadow: ${({ theme }) => theme.elevations.abnElevationWell};
    max-width: 624px;
  `};

  ${mq.from.breakpoint.XL`
    padding: ${Spacings.XL};
  `};
`;

const ImageContainer = styled.div`
  height: 72px;
  margin-right: ${Spacings.S};
  width: 72px;

  img {
    height: inherit;
    left: -8px;
    position: relative;
    top: -8px;
    width: inherit;
  }
`;

const Title = styled(H3)`
  margin: 0 0 ${Spacings.XS};
`;

const StyledBody = styled(Body)`
  display: flex;
  flex: 1;
  flex-direction: column;
  position: relative;

  ul {
    margin-top: 0;
    padding-top: 0;
    padding-bottom: 0;

    li:last-of-type {
      margin-bottom: 0;
    }
  }

  p ~ ul {
    margin-top: ${Spacings.M};
    padding-top: 0;
  }

  p:last-of-type,
  ul:last-of-type {
    margin-bottom: 0;
  }

  &:last-child p:not(~ ul) {
    a {
      display: inline-flex;
      flex: 1;
      flex-direction: column;
      justify-content: flex-end;
    }
  }
`;

const StyledSecondaryButton = styled(SecondaryButton)`
  display: inline-flex;
  margin-top: ${Spacings.M};

  ${mq.from.breakpoint.M`
    margin-top: ${Spacings.L};
  `};
`;
